import type { IUserInfoRes } from '@/api/types/login';
import { defineStore } from 'pinia';
import {
  getInfo,
} from '@/api/account';
import { RoleEnum } from '@/enums/roleEnum';
import { ProjectEnum } from '@/project';
import { formatMobile } from '@/utils/phone-verify';

interface UserState {
  token: string
  site: string
  name: string
  avatar: string
  // like [ 'sys:user:add', 'sys:user:update' ]
  perms: string[]
  // menus: RouteRecordRaw[];
  // topMenus: TopMenu[];
  userInfo: Partial<API.AdminUserInfo>
  sellerId: number
  apiUrl: string
  imgApiUrl: string
  pcUrl: string
  logoUrl: string
  faviconIcoUrl: string
  siteInfo: Partial<API.GetInfo>
  // chat-specific fields
  id: number
  chatRole: string
  siteId: number | null
  /** 用户已开通「档口优惠」的站点 id 列表 */
  userGoodsDiscountSiteList: number[]
  /** 用户已开通「我的云仓」的站点 id 列表 */
  userGoodsCloudSiteList: number[]
}

// 初始化状态
const userInfoState: IUserInfoRes = {
  userId: -1,
  username: '',
  nickname: '',
  avatar: '/static/images/default-avatar.png',
};

const env = import.meta.env;
const { VITE_APP_TYPE, VITE_APP_BASEURL } = env;

export const useUserStore = defineStore({
  id: 'user',
  state: (): UserState => ({
    token: '',
    site: '',
    name: '',
    avatar: '',
    perms: [],
    // menus: [],
    // topMenus: [],
    userInfo: {},
    sellerId: 0,
    apiUrl: '',
    imgApiUrl: '',
    logoUrl: '',
    pcUrl: '',
    faviconIcoUrl: '',
    siteInfo: {},
    id: 0,
    chatRole: '',
    siteId: null,
    userGoodsDiscountSiteList: [],
    userGoodsCloudSiteList: [],
  }),
  getters: {
    getToken(): string {
      return this.token;
    },
    getAvatar(): string {
      return this.avatar;
    },
    getName(): string {
      return this.name;
    },
    getPerms(): string[] {
      return this.perms;
    },
    getMobileOmit(): string {
      const mobile = this.userInfo?.mobile || '';
      return formatMobile(mobile);
    },
    getEvn() {
      return env;
    },
    getProject(): ProjectEnum {
      if (VITE_APP_TYPE === ProjectEnum.MAIN) {
        // 主站，可能需要通过判断当前站点数量是否为站点独立站
        // 如当前环境为主站，但是获取到的站点只有一个，即必须将当前环境修改为站点独立站
      }
      return VITE_APP_TYPE as ProjectEnum;
    },
    // chat-specific getters
    isService(): boolean {
      return this.chatRole === RoleEnum.KEFU;
    },
    isClient(): boolean {
      return this.chatRole === RoleEnum.USER;
    },
    hasLogin(): boolean {
      return !!this.token;
    },
    // 用户仓列表（商品库存功能需不为空才可用）
    userWarehouseList(): number[] {
      return this.userInfo?.userWarehouseList || [];
    },
  },
  actions: {
    setChatUserInfo(info: { id: number, role: number, site_id: number }) {
      this.id = info.id;
      this.chatRole = info.role ? RoleEnum.KEFU : RoleEnum.USER;
      this.siteId = info.site_id;
    },
    /* 登录成功之后，获取用户信息以及生成权限路由 */
    async fetchUserInfo() {
      try {
        const [userInfo] = await Promise.all([getInfo()]);
        this.name = userInfo.nickname;
        this.avatar = userInfo.image;
        this.sellerId = userInfo.sellerId;
        this.id = userInfo.id || userInfo.sellerId || 0;
        this.userInfo = userInfo;
        this.userGoodsDiscountSiteList = userInfo.userGoodsDiscountSiteList || [];
        this.userGoodsCloudSiteList = userInfo.userGoodsCloudSiteList || [];
        return { menus: [], perms: [], userInfo };
      }
      catch (e) {
        return Promise.reject(e);
      }
    },
    clearUserInfo() {
      this.userInfo = {};
      uni.removeStorageSync('user');
    },
  },
  persist: true,
});
